import { useMemo } from 'react';
import { FormDialog, toast } from '@core/components';
import { type Order, createOrder, patchOrderDetails } from '../../api/order';
import OrderDetailsForm, {
  EMPTY_ORDER_DETAILS_FORM_VALUES,
  type OrderDetailsFormValues,
  orderToDetailsFormValues,
} from './OrderDetailsForm';

interface OrderDialogProps {
  open: boolean;
  onClose: () => void;
  order?: Order | null;
  onSuccess?: (order: Order) => void;
}

const OrderDialog = ({ open, onClose, order, onSuccess }: OrderDialogProps) => {
  const defaultValues = useMemo<OrderDetailsFormValues>(
    () =>
      order ? orderToDetailsFormValues(order) : EMPTY_ORDER_DETAILS_FORM_VALUES,
    [order]
  );

  const onSubmit = async (data: OrderDetailsFormValues) => {
    const details = {
      customer: Number(data.customer),
      dueDate: data.dueDate || null,
      notes: data.notes.trim() || null,
    };

    try {
      if (!order) {
        const created = await createOrder({ ...details, items: [] });
        toast.success('Order created successfully');
        onSuccess?.(created);
        return;
      }
      const updated = await patchOrderDetails(order.id, details);
      toast.success('Order updated successfully');
      onSuccess?.(updated);
    } catch (error) {
      toast.apiError(
        !order ? 'Failed to create order.' : 'Failed to update order.',
        error
      );
      throw error;
    }
  };

  return (
    <FormDialog<OrderDetailsFormValues>
      open={open}
      onClose={onClose}
      title={!order ? 'New order' : 'Edit order'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={!order ? 'Create' : 'Save'}
      cancelText="Cancel"
    >
      <OrderDetailsForm />
    </FormDialog>
  );
};

export default OrderDialog;
